import { CommonModule, NgClass } from '@angular/common';
import { Component, ElementRef, ViewChild, AfterViewInit, OnDestroy, OnChanges, SimpleChanges, forwardRef, Input, EventEmitter, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import flatpickr from 'flatpickr';
import { Instance } from 'flatpickr/dist/types/instance';
import { Options } from 'flatpickr/dist/types/options';
import { CommonService } from '../../service/common.service';

@Component({
    selector: 'app-date-time-picker',
    templateUrl: './date-time-picker.component.html',
    styleUrls: ['./date-time-picker.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DateTimePickerComponent),
            multi: true
        }
    ],
    standalone: false
})
export class DateTimePickerComponent implements AfterViewInit, OnDestroy, OnChanges, ControlValueAccessor {
  @ViewChild('dateInput') dateInput!: ElementRef;
  @Input() placeholder: string = 'Select...';
  @Input() mode: 'date' | 'datetime' | 'range' | 'time' = 'date'; 
  @Input() customConfig: Options = {}; 
  @Input() cssClass: string = ''; 
  @Input() fontFamily?: string = 'poppinsLight'; 
  @Input() minDate: Date | string | undefined;
  @Input() maxDate: Date | string | undefined;
  @Input() isDisabled: boolean = false;
  @Input() use24HourTime: boolean = false;
  
  @Output() dateSelected = new EventEmitter<string>();

  baseClasses = 'block w-full border  border-lineGray rounded-lg transition-colors duration-200 focus:outline-none focus:ring-1 focus:ring-primary_Bg_Button_Color ';

  private flatpickrInstance!: Instance;
  public value: string = '';
  private readonly staffingNewUxClass = 'staffing-newux-styles';

  constructor(public commonService:CommonService){}

  get showDateBadge(): boolean {
    return this.mode === 'date' && this.cssClass.split(/\s+/).includes('dateBadgeStyles');
  }

  get dateBadgeLabel(): string {
    const date = flatpickr.parseDate(this.value, 'Y-m-d');
    return date ? flatpickr.formatDate(date, 'M j, Y') : this.value;
  }

  clearDateBadge(event: MouseEvent): void {
    event.stopPropagation();
    if (this.isDisabled || !this.flatpickrInstance) return;
    this.flatpickrInstance.clear();
  }

  onChange: any = () => {};
  onTouch: any = () => {};

  ngAfterViewInit(): void {
    this.initFlatpickr();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.flatpickrInstance) {
      if (changes['minDate']) {
        this.flatpickrInstance.set('minDate', this.minDate);
      }
      if (changes['maxDate']) {
        this.flatpickrInstance.set('maxDate', this.maxDate);
      }
      if (changes['isDisabled']) {
        this.setDisabledState(this.isDisabled);
      }
      if (changes['mode'] || changes['customConfig'] || changes['use24HourTime']) {
        this.initFlatpickr();
      }
      if (changes['cssClass']) {
        this.syncCalendarStyleClass();
      }
    }
  }

  ngOnDestroy(): void {
    if (this.flatpickrInstance) {
      this.flatpickrInstance.destroy();
    }
  }

  private initFlatpickr(): void {
    if (this.flatpickrInstance) {
      this.flatpickrInstance.destroy();
    }

    const modeConfig = this.getModeConfig();
    const finalConfig: Options = {
      ...modeConfig,
      ...this.customConfig,
      minDate: this.minDate,
      maxDate: this.maxDate,
      ...(this.mode === 'datetime' ? {
        closeOnSelect: false,
      } : {}),
      ...(this.mode !== 'time' ? {
        disableMobile: true,
        plugins: [...(this.customConfig.plugins || []), (instance: Instance) => this.calendarPanel(instance)]
      } : {}),
      onChange: (selectedDates, dateStr) => {
        if (this.mode === 'datetime' && this.flatpickrInstance?.isOpen) return;
        this.commitValue(dateStr);
      }
    };

    this.flatpickrInstance = flatpickr(this.dateInput.nativeElement, finalConfig);
    this.syncCalendarStyleClass();

    // Sync disabled state on init
    this.setDisabledState(this.isDisabled);

    if (this.value) {
      this.flatpickrInstance.setDate(this.value, false);
    }
  }

  private commitValue(value: string): void {
    this.value = value;
    this.onChange(value);
    this.onTouch();
    this.dateSelected.emit(value);
  }

  private calendarPanel(instance: Instance): Options {
    const isDatetime = this.mode === 'datetime';
    const use24HourTime = instance.config.time_24hr;
    let hour: HTMLButtonElement;
    let minute: HTMLButtonElement;
    let period: HTMLButtonElement | undefined;
    let apply: HTMLButtonElement;
    const refreshControls: Array<() => void> = [];
    const closeMenus: Array<() => void> = [];
    let syncMonth = () => {};

    const sync = () => {
      syncMonth();
      if (!hour) return;
      const date = instance.selectedDates[0];
      const hours = date ? date.getHours() : instance.config.defaultHour;
      hour.value = String(use24HourTime ? hours : hours % 12 || 12);
      minute.value = String(date ? date.getMinutes() : instance.config.defaultMinute);
      if (period) period.value = hours >= 12 ? 'PM' : 'AM';
      refreshControls.forEach(refresh => refresh());
      apply.disabled = !date;
    };

    const updateTime = () => {
      let hours = Number(hour.value);
      if (period) hours = hours % 12 + (period.value === 'PM' ? 12 : 0);
      const minutes = Number(minute.value);
      instance.config.defaultHour = hours;
      instance.config.defaultMinute = minutes;
      instance.hourElement.value = String(use24HourTime ? hours : hours % 12 || 12);
      instance.minuteElement.value = String(minutes);
      if (instance.amPM) instance.amPM.textContent = hours >= 12 ? 'PM' : 'AM';
      if (instance.selectedDates[0]) {
        let date = new Date(instance.selectedDates[0]);
        date.setHours(hours, minutes, 0, 0);
        // Keep time selections inside the existing datetime limits.
        if (instance.config.minDate && date < instance.config.minDate) date = new Date(instance.config.minDate);
        if (instance.config.maxDate && date > instance.config.maxDate) date = new Date(instance.config.maxDate);
        instance.setDate(date, false);
        sync();
      }
    };

    return {
      onReady: () => {
        const calendar = instance.calendarContainer;
        calendar.classList.add('custom-month-picker-calendar');
        if (isDatetime) calendar.classList.add('datetime-picker-calendar');
        const panel = document.createElement('div');
        panel.className = 'datetime-picker-time-panel';
        // Keep time-control keyboard events separate from calendar navigation.
        const nativeKeyboard = (event: KeyboardEvent) => {
          if (event.key !== 'Escape') event.stopPropagation();
        };
        panel.addEventListener('keydown', nativeKeyboard);
        const title = document.createElement('h3');
        title.textContent = 'Select time';
        panel.appendChild(title);

        const select = (
          labelText: string,
          values: string[],
          parent: HTMLElement = panel,
          onSelect: () => void = updateTime,
          displayValue = (value: string) => /^\d+$/.test(value) ? value.padStart(2, '0') : value,
          isAvailable = (_value: string) => true
        ) => {
          const field = document.createElement('div');
          field.className = 'datetime-picker-time-field';
          const label = document.createElement('span');
          label.textContent = labelText;
          const control = document.createElement('button');
          control.type = 'button';
          control.className = 'datetime-picker-time-trigger hoverShadow';
          control.setAttribute('aria-label', labelText);
          control.setAttribute('aria-haspopup', 'listbox');
          control.setAttribute('aria-expanded', 'false');
          const menu = document.createElement('div');
          menu.className = 'datetime-picker-time-menu hoverShadowOnDropdown';
          menu.setAttribute('role', 'listbox');
          menu.setAttribute('aria-label', labelText);
          menu.hidden = true;
          const close = () => {
            menu.hidden = true;
            control.setAttribute('aria-expanded', 'false');
          };
          closeMenus.push(close);
          const options = values.map(value => {
            const option = document.createElement('button');
            option.type = 'button';
            option.setAttribute('role', 'option');
            option.tabIndex = -1;
            option.value = value;
            option.textContent = displayValue(value);
            option.addEventListener('click', () => {
              control.value = value;
              onSelect();
              refreshControls.forEach(refresh => refresh());
              close();
              control.focus();
            });
            menu.appendChild(option);
            return option;
          });
          refreshControls.push(() => {
            control.textContent = displayValue(control.value);
            options.forEach(option => {
              option.hidden = !isAvailable(option.value);
              option.setAttribute('aria-selected', String(option.value === control.value));
            });
          });
          const open = () => {
            syncMonth();
            refreshControls.forEach(refresh => refresh());
            closeMenus.forEach(closeMenu => closeMenu());
            menu.hidden = false;
            control.setAttribute('aria-expanded', 'true');
            const selected = options.find(option => !option.hidden && option.value === control.value)
              || options.find(option => !option.hidden);
            if (!selected) { close(); return; }
            selected.focus();
            selected.scrollIntoView({ block: 'nearest' });
          };
          control.addEventListener('click', () => menu.hidden ? open() : close());
          control.addEventListener('keydown', event => {
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
              event.preventDefault();
              open();
            }
          });
          menu.addEventListener('keydown', event => {
            const availableOptions = options.filter(option => !option.hidden);
            const index = availableOptions.indexOf(document.activeElement as HTMLButtonElement);
            let next = index;
            if (event.key === 'ArrowDown') next = (index + 1) % availableOptions.length;
            else if (event.key === 'ArrowUp') next = (index - 1 + availableOptions.length) % availableOptions.length;
            else if (event.key === 'Home') next = 0;
            else if (event.key === 'End') next = availableOptions.length - 1;
            else if (event.key === 'Escape') {
              event.preventDefault();
              event.stopPropagation();
              close();
              control.focus();
              return;
            } else return;
            event.preventDefault();
            availableOptions[next]?.focus();
            availableOptions[next]?.scrollIntoView({ block: 'nearest' });
          });
          field.addEventListener('focusout', event => {
            if (!field.contains(event.relatedTarget as Node | null)) close();
          });
          calendar.addEventListener('click', event => {
            if (!field.contains(event.target as Node)) close();
          });
          field.append(label, control, menu);
          parent.appendChild(field);
          return control;
        };
        if (isDatetime) {
          hour = select('Hour', Array.from({ length: use24HourTime ? 24 : 12 }, (_, index) => String(index + (use24HourTime ? 0 : 1))));
          minute = select('Minute', Array.from({ length: 60 }, (_, index) => String(index)));
          if (!use24HourTime) period = select('AM/PM', ['AM', 'PM']);
          calendar.appendChild(panel);
        }

        const nativeMonth = instance.monthsDropdownContainer;
        if (nativeMonth) {
          const monthContainer = nativeMonth.parentElement!;
          const month = select(
            'Month',
            Array.from({ length: 12 }, (_, index) => String(index)),
            monthContainer,
            () => instance.changeMonth(Number(month.value), false),
            value => instance.l10n.months[instance.config.shorthandCurrentMonth ? 'shorthand' : 'longhand'][Number(value)],
            value => {
              const min = instance.config.minDate;
              const max = instance.config.maxDate;
              return !(min && instance.currentYear === min.getFullYear() && Number(value) < min.getMonth())
                && !(max && instance.currentYear === max.getFullYear() && Number(value) > max.getMonth());
            }
          );
          const field = month.parentElement!;
          field.className = 'datetime-picker-month-field';
          field.firstElementChild!.remove();
          field.addEventListener('keydown', nativeKeyboard);
          monthContainer.insertBefore(field, nativeMonth);
          nativeMonth.style.display = 'none';
          syncMonth = () => {
            month.value = String(instance.currentMonth);
            refreshControls.forEach(refresh => refresh());
          };
        }

        if (!isDatetime) {
          sync();
          return;
        }

        const footer = document.createElement('div');
        footer.className = 'datetime-picker-actions';
        footer.addEventListener('keydown', nativeKeyboard);
        const cancel = document.createElement('button');
        cancel.type = 'button';
       cancel.className = 'cancel-picker-apply bg-primary_Bg_Button_Color'
        cancel.textContent = 'Cancel';
        cancel.addEventListener('click', () => {
          instance.clear(false);
          this.commitValue('');
          sync();
          instance.close();
        });
        apply = document.createElement('button');
        apply.type = 'button';
        apply.className = 'datetime-picker-apply bg-primary_Bg_Button_Color';
        apply.textContent = 'Apply';
        apply.addEventListener('click', () => {
          if (!instance.selectedDates[0]) return;
          this.commitValue(instance.formatDate(instance.selectedDates[0], 'Y-m-d\\TH:i'));
          instance.close();
        });
        footer.append(cancel, apply);
        calendar.appendChild(footer);
        sync();
      },
      onOpen: () => {
        if (isDatetime && !instance.selectedDates.length) {
          const now = new Date();
          instance.config.defaultHour = now.getHours();
          instance.config.defaultMinute = now.getMinutes();
          instance.hourElement.value = String(use24HourTime ? now.getHours() : now.getHours() % 12 || 12);
          instance.minuteElement.value = String(now.getMinutes());
          if (instance.amPM) instance.amPM.textContent = now.getHours() >= 12 ? 'PM' : 'AM';
        }
        sync();
      },
      onChange: sync,
      onValueUpdate: sync,
      onMonthChange: () => syncMonth(),
      onYearChange: () => syncMonth(),
      onClose: () => {
        closeMenus.forEach(close => close());
        if (!isDatetime) return;
        // Closing without Apply discards the draft, including outside clicks/Escape.
        if (this.value) instance.setDate(this.value, false);
        else instance.clear(false);
        this.onTouch();
      }
    };
  }

  private syncCalendarStyleClass(): void {
    const calendar = this.flatpickrInstance?.calendarContainer;
    if (!calendar) return;

    const hasStaffingNewUxStyles = this.cssClass
      .split(/\s+/)
      .includes(this.staffingNewUxClass);

    calendar.classList.toggle(
      this.staffingNewUxClass,
      hasStaffingNewUxStyles
    );
  }

  private getModeConfig(): Options {
    // Get the current time to use as the default for the time pickers
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();

    switch (this.mode) {
      case 'range':
        return { 
          mode: 'range', // Tells flatpickr to allow selecting a start and end date
          enableTime: false, 
          noCalendar: false, 
          dateFormat: 'Y-m-d', 
          altInput: true,  
          altFormat: 'F j, Y'  // User sees: "April 30, 2026 to May 5, 2026"
        };
      case 'datetime':
        return { 
          enableTime: true, 
          noCalendar: false, 
          minuteIncrement: 1, // Increment mins by 1 for more precise time selection
          dateFormat: 'Y-m-d\\TH:i', 
          time_24hr: this.use24HourTime , 
          altInput: true,  
          // altFormat: 'F j, Y at H:i', 
          altFormat: this.use24HourTime ? 'F j, Y at H:i' : 'F j, Y at h:i K',
          defaultHour: currentHour,
          defaultMinute: currentMinute,  

          // Optional: If you want to ensure the time updates to the EXACT minute 
          // they open the dropdown (in case they left the page open for hours)
          onOpen: function(selectedDates, dateStr, instance) {
            if (selectedDates.length === 0) {
              const exactNow = new Date();
              instance.config.defaultHour = exactNow.getHours();
              instance.config.defaultMinute = exactNow.getMinutes();
            }
          }
        };
      case 'time':
        return { 
          enableTime: true, 
          noCalendar: true, 
          minuteIncrement: 1, // Increment mins by 1 for more precise time selection
          // dateFormat: 'H:i', 
          dateFormat:  this.use24HourTime ? 'H:i' : 'h:i K',
          time_24hr: this.use24HourTime ,
          defaultHour: currentHour,
          defaultMinute: currentMinute,
        };
      case 'date':
      default:
        return { 
          enableTime: false, 
          noCalendar: false, 
          dateFormat: 'Y-m-d',
          altInput: true,  
          altFormat: 'F j, Y',  
        };
    }
  }

  writeValue(value: any): void {
  this.value = value || '';

  if (!this.flatpickrInstance) return;

  if (value) {
    // false = do not trigger flatpickr onChange
    this.flatpickrInstance.setDate(value, false);
  } else {
    this.flatpickrInstance.clear(false);
  }
}

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }
  
  setDisabledState(isDisabled: boolean): void {
  this.isDisabled = isDisabled; 
  
  if (this.flatpickrInstance) {
    const input = this.flatpickrInstance.altInput || this.flatpickrInstance.input;
    
    if (isDisabled) {
      input.setAttribute('disabled', 'disabled');
    } else {
      input.removeAttribute('disabled');
    }

    // Force the UI update regardless of Angular's change detection
    input.classList.toggle('bg-gray-100', isDisabled);
    input.classList.toggle('opacity-50', isDisabled);
    input.classList.toggle('cursor-not-allowed', isDisabled);
    
    input.classList.toggle('bg-white', !isDisabled);
    input.classList.toggle('cursor-pointer', !isDisabled);
  }
}
}
